import { useMutation } from '@tanstack/react-query';
import { generateKeyBetween } from 'fractional-indexing';
import type { QuoteDTO } from '@setvi/shared/services/react-query';
import { moveQuoteV2ItemsMutation } from 'Services/Mutations/QuotesV2';
import {
  moveItemToSectionOptimistically,
  getLastPositionInContainer
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

interface MoveItemVariables {
  itemId: string;
  targetSectionId: string | null;
}

/**
 * Move item to a different section via V2 move endpoint.
 * Applies optimistic update immediately, then syncs with server.
 */
export const useMoveItem = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = moveQuoteV2ItemsMutation();

  return useMutation({
    mutationFn: ({ itemId, targetSectionId }: MoveItemVariables) => {
      const items = cache.getItems() || [];
      const lastPos = getLastPositionInContainer(items, targetSectionId);
      const position = generateKeyBetween(lastPos, null);

      return mutationFn({
        quoteId,
        moves: [
          {
            itemId,
            targetSectionId,
            position
          }
        ]
      });
    },

    onMutate: async ({ itemId, targetSectionId }: MoveItemVariables) => {
      await cache.cancel();

      const previousQuote = cache.getQuote();
      const previousItems = cache.getItems();

      const composed = previousQuote
        ? ({
            ...previousQuote,
            items: previousItems || []
          } as unknown as QuoteDTO)
        : undefined;

      const updated = moveItemToSectionOptimistically(
        composed,
        itemId,
        targetSectionId
      );

      if (updated) {
        const { items: updatedItems, ...quoteData } = updated as any;
        cache.setQuote(() => quoteData);
        cache.setItems(() => updatedItems);
      }

      return { previousQuote, previousItems };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: () => {
      cache.invalidate();
    }
  });
};
